import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState, Card, Modal, Input, Select, Textarea, PrimaryButton, GhostButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Swords, Plus, ArrowLeft, Youtube, Trophy, MapPin, Share2, Calendar } from "lucide-react";
import { toast } from "sonner";

export default function Matches() {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [myTeams, setMyTeams] = useState([]);
  const [fbs, setFbs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", sport: "", format: "", home_team_id: "", venue_name: "", unit_id: "", date: "", start: "" });

  const load = () => api.get("/matches").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => {
    load();
    api.get("/me/teams").then((r) => setMyTeams(r.data.filter((t) => t.my_status === "active"))).catch(() => {});
    api.get("/me/facility-bookings").then((r) => setFbs(r.data.filter((b) => b.status === "confirmed"))).catch(() => {});
  }, []);
  if (!list) return <Spinner />;

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (form.unit_id) { const fb = fbs.find((b) => b.unit_id === form.unit_id); payload.venue_name = fb ? `${fb.facility_name} · ${fb.unit_name}` : form.venue_name; }
      const { data } = await api.post("/matches", payload);
      toast.success("Match created"); setOpen(false); navigate(`/app/matches/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/play")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Back to Play</button>
      <PageHeader title="Matches" subtitle="Create matches, find opponents, record scores" action={<PrimaryButton data-testid="create-match-btn" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Create match</PrimaryButton>} />

      {list.length === 0 ? <EmptyState icon={Swords} title="No matches yet" text="Create a match and invite an opponent." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((m) => (
            <button key={m.id} data-testid={`match-card-${m.id}`} onClick={() => navigate(`/app/matches/${m.id}`)}
              className="rounded-2xl border border-border bg-card p-5 text-left transition-all hover:-translate-y-1 hover:border-primary/50">
              <div className="flex items-start justify-between"><h3 className="font-display text-lg font-semibold">{m.title}</h3><StatusBadge status={m.status} /></div>
              <p className="mt-1 text-sm capitalize text-primary">{m.sport}{m.format ? ` · ${m.format}` : ""}</p>
              {m.home_team_name && <p className="mt-2 text-sm">{m.home_team_name}{m.away_team_name ? ` vs ${m.away_team_name}` : ""}</p>}
              {m.status === "completed" && m.home_score != null && <p className="mt-1 font-display text-xl font-bold text-primary">{m.home_score} – {m.away_score}</p>}
              <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground"><Calendar className="h-3 w-3" /> {m.date} {m.start}</p>
              {m.youtube_id && <p className="mt-1 flex items-center gap-1 text-xs text-rose-400"><Youtube className="h-3 w-3" /> Livestream</p>}
            </button>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Create a match" wide>
        <form onSubmit={create} className="grid gap-4 sm:grid-cols-2">
          <Input label="Match title" testId="match-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sm:col-span-2" />
          <Input label="Sport" testId="match-sport" required value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} />
          <Input label="Format" testId="match-format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} placeholder="e.g. 5-a-side" />
          <Select label="Home team" testId="match-home-team" value={form.home_team_id} onChange={(e) => setForm({ ...form, home_team_id: e.target.value })}>
            <option value="">- none -</option>{myTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </Select>
          <Select label="Venue (your confirmed bookings)" testId="match-venue" value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}>
            <option value="">- set manually -</option>{fbs.map((b) => <option key={b.id} value={b.unit_id}>{b.facility_name} · {b.unit_name} ({b.date})</option>)}
          </Select>
          {!form.unit_id && <Input label="Venue name" testId="match-venue-name" value={form.venue_name} onChange={(e) => setForm({ ...form, venue_name: e.target.value })} />}
          <Input label="Date" testId="match-date" type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          <Input label="Start time" testId="match-start" type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
          <div className="flex justify-end gap-2 sm:col-span-2"><GhostButton type="button" onClick={() => setOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-match-btn" type="submit">Create</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}

export function MatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [m, setM] = useState(null);
  const [scoreOpen, setScoreOpen] = useState(false);
  const [sc, setSc] = useState({ home_score: 0, away_score: 0, result: "" });
  const [yt, setYt] = useState("");

  const load = () => api.get(`/matches/${id}`).then((r) => setM(r.data)).catch(() => navigate("/app/matches"));
  useEffect(() => { load(); }, [id]);
  if (!m) return <Spinner />;
  const isOrganizer = m.organizer_id === user?.id || hasRole("admin", "ops");

  const setStatus = async (status) => { try { await api.patch(`/matches/${id}/status`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };
  const saveScore = async (e) => { e.preventDefault(); try { await api.post(`/matches/${id}/score`, { home_score: Number(sc.home_score), away_score: Number(sc.away_score), result: sc.result }); toast.success("Score recorded"); setScoreOpen(false); load(); } catch (err) { toast.error(apiError(err)); } };
  const attachYt = async (e) => { e.preventDefault(); try { await api.post(`/matches/${id}/youtube`, { url: yt }); toast.success("Livestream attached"); setYt(""); load(); } catch (err) { toast.error(apiError(err)); } };
  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Match link copied"); };

  return (
    <div className="animate-fadeUp">
      <button onClick={() => navigate("/app/matches")} className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> All matches</button>
      <PageHeader title={m.title} subtitle={`${m.reference} · ${m.sport}`} action={<div className="flex items-center gap-2"><StatusBadge status={m.status} /><GhostButton className="py-2" onClick={share}><Share2 className="h-4 w-4" /> Share</GhostButton></div>} />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-around py-4 text-center">
            <div className="flex-1"><p className="font-display text-xl font-semibold">{m.home_team_name || "Home"}</p></div>
            <div className="px-4">
              {m.status === "completed" && m.home_score != null
                ? <p data-testid="match-score" className="font-display text-4xl font-extrabold text-primary">{m.home_score} – {m.away_score}</p>
                : <p className="font-mono text-sm text-muted-foreground">vs</p>}
            </div>
            <div className="flex-1"><p className="font-display text-xl font-semibold">{m.away_team_name || "Opponent"}</p></div>
          </div>
          {m.result && <p className="text-center text-sm font-medium text-emerald-400">{m.result}</p>}
          <div className="mt-4 space-y-1 border-t border-border pt-4 text-sm">
            <Row icon={Calendar} v={`${m.date} ${m.start || ""}`} />
            {(m.unit_name || m.venue_name) && <Row icon={MapPin} v={m.unit_name || m.venue_name} />}
          </div>
          {isOrganizer && (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
              {["open", "awaiting_opponent", "confirmed", "cancelled"].map((s) => (
                <GhostButton key={s} data-testid={`match-status-${s}`} className="py-1.5 text-xs capitalize" onClick={() => setStatus(s)}>{s.replace("_", " ")}</GhostButton>
              ))}
              {m.status !== "completed" && <PrimaryButton data-testid="record-score-btn" className="py-1.5 text-xs" onClick={() => setScoreOpen(true)}><Trophy className="h-4 w-4" /> Record score</PrimaryButton>}
            </div>
          )}
        </Card>

        <Card>
          <div className="mb-3 flex items-center gap-2"><Youtube className="h-5 w-5 text-rose-400" /><h3 className="font-display text-lg font-semibold">Livestream</h3></div>
          {m.youtube_id ? (
            <div className="aspect-video overflow-hidden rounded-xl border border-border" data-testid="match-youtube-embed">
              <iframe title="livestream" width="100%" height="100%" src={`https://www.youtube.com/embed/${m.youtube_id}`} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          ) : <p className="text-sm text-muted-foreground">No livestream attached yet.</p>}
          {isOrganizer && (
            <form onSubmit={attachYt} className="mt-3">
              <Input testId="youtube-url-input" placeholder="Paste YouTube URL" value={yt} onChange={(e) => setYt(e.target.value)} />
              <PrimaryButton data-testid="attach-youtube-btn" type="submit" className="mt-2 w-full py-2"><Youtube className="h-4 w-4" /> Attach stream</PrimaryButton>
              <p className="mt-2 text-xs text-muted-foreground">SPORA1 embeds the stream from YouTube; it does not host the broadcast.</p>
            </form>
          )}
        </Card>
      </div>

      <Modal open={scoreOpen} onClose={() => setScoreOpen(false)} title="Record score">
        <form onSubmit={saveScore} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label={m.home_team_name || "Home"} testId="score-home" type="number" value={sc.home_score} onChange={(e) => setSc({ ...sc, home_score: e.target.value })} />
            <Input label={m.away_team_name || "Away"} testId="score-away" type="number" value={sc.away_score} onChange={(e) => setSc({ ...sc, away_score: e.target.value })} />
          </div>
          <Input label="Result note" testId="score-result" value={sc.result} onChange={(e) => setSc({ ...sc, result: e.target.value })} placeholder="e.g. Falcons win" />
          <div className="flex justify-end gap-2"><GhostButton type="button" onClick={() => setScoreOpen(false)}>Cancel</GhostButton><PrimaryButton data-testid="save-score-btn" type="submit">Save & complete</PrimaryButton></div>
        </form>
      </Modal>
    </div>
  );
}

const Row = ({ icon: Icon, v }) => <p className="flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /> {v}</p>;
